import { metaPixelId } from '@/config/meta-pixel';
import { isMetaPixelPath, needsSignedOut } from '@/marketing/meta-pixel-routes';
import { hasConsentFor, onConsentChange } from '@/utils/cookieConsent';
import { isSubdomainSite } from '@/utils/subdomain';

/*
 * The Meta (Facebook) Pixel, for measuring ads and sign-ups. Every call is a no-op unless ALL of these hold at that moment:
 *  - the build has a pixel id (VITE_META_PIXEL_ID, src/config/meta-pixel.ts; only production sets it),
 *  - the visitor accepted analytics cookies in the cookie banner (re-read on every call, so a withdrawal counts at once) and the browser sends no
 *    Global Privacy Control signal,
 *  - the current page is on the allow-list (src/marketing/meta-pixel-routes.ts) and on the main site, not a business's subdomain; on the sign-up page
 *    the visitor must be known signed out; a same-origin referrer must itself be an allow-listed page,
 *  - it is a real visit, not the build-time prerender snapshot.
 * Otherwise nothing is loaded, no cookie is set and no request is made. Meta's script is created here (no inline <script>: the production CSP only
 * allows hashed inline scripts) and never through a <noscript> image. Parameters are restricted to a few coarse keys (sanitizeParams): never an email,
 * a name or any other personal data. Never throws: analytics never breaks a page.
 */

export const META_PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

export type MetaPixelEvent = 'PageView' | 'Lead' | 'CompleteRegistration';

export interface MetaPixelParams {
  content_name?: string;
  content_category?: string;
  status?: boolean;
}

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
  allowDuplicatePageViews?: boolean;
};
type PixelWindow = Window & { fbq?: Fbq; _fbq?: Fbq; __PRERENDER__?: boolean };
const win = () => window as PixelWindow;

/** A slug or an offer id: letters, digits, - and _ only, with at least one letter. That shape can never carry an email, a name or a phone number. */
const SAFE_TEXT = /^(?=.*[A-Za-z])[A-Za-z0-9_-]{1,100}$/;

/** Keeps only `content_name`, `content_category` (slug-shaped strings) and `status` (a boolean); everything else is dropped. */
export function sanitizeParams(params?: Record<string, unknown>): MetaPixelParams | undefined {
  if (!params) return undefined;
  const out: MetaPixelParams = {};
  for (const key of ['content_name', 'content_category'] as const) {
    const v = params[key];
    if (typeof v === 'string' && SAFE_TEXT.test(v)) out[key] = v;
  }
  if (typeof params.status === 'boolean') out.status = params.status;
  return Object.keys(out).length ? out : undefined;
}

let loaded = false;
let revoked = false;
/** Set by MetaPixelTracker from the auth context: true only once auth has resolved to "no user". */
let signedOut = false;

/** The auth context resolved (or changed): the sign-up page only counts for a visitor known to be signed out. */
export function setSignedOut(value: boolean): void {
  signedOut = value;
}

/**
 * Meta also sends document.referrer. A same-origin referrer (a full page load from inside the site, such as a session that ended on an app screen) must
 * itself be an allow-listed page with no email in it; a referrer from another site (an ad, a search engine) is fine.
 */
function referrerSafe(): boolean {
  const ref = document.referrer;
  if (!ref) return true;
  let url: URL;
  try {
    url = new URL(ref);
  } catch {
    return !ref.includes(window.location.host);
  }
  if (url.origin !== window.location.origin) return true;
  return isMetaPixelPath(url.pathname) && !/@|%40/i.test(url.search + url.hash);
}
let currentPath: string | null = null;
let pageViewFor: string | null = null;

/** May the pixel load and fire on this page right now? */
export function metaPixelAllowed(pathname: string = window.location.pathname): boolean {
  try {
    if (!metaPixelId()) return false;
    if (win().__PRERENDER__ === true) return false;
    if (hasConsentFor('analytics') !== true) return false;
    // a Global Privacy Control signal is an opt-out of "sharing" (California): the privacy policy promises the pixel does not load then
    if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return false;
    if (!isMetaPixelPath(pathname)) return false;
    if (needsSignedOut(pathname) && !signedOut) return false;
    if (!referrerSafe()) return false;
    // Meta records the page address: an address that carries an email (a mail link with ?email=...) is never sent
    if (/@|%40/i.test(window.location.search + window.location.hash)) return false;
    return !isSubdomainSite();
  } catch {
    return false;
  }
}

/** Meta's queueing stub (the same as their base code) plus our settings, then their script. Once per page load. */
function load(id: string): Fbq {
  const w = win();
  if (!w.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod(...args);
      else fbq.queue.push(args);
    } as Fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    w.fbq = fbq;
    if (!w._fbq) w._fbq = fbq;
  }
  const fbq = w.fbq;
  if (loaded) return fbq;
  loaded = true;
  // Meta would otherwise send its own PageView on every history change, app screens included (after sign-up the SPA moves to /admin/onboarding):
  // PageViews come only from routeChanged below, on allowed pages.
  fbq.disablePushState = true;
  // our own dedup decides (a return visit to a page in the same session is a new view)
  fbq.allowDuplicatePageViews = true;
  // no automatic events and no automatic advanced matching: Meta would otherwise read form fields (the email box of the template form)
  fbq('set', 'autoConfig', false, id);
  fbq('init', id);
  const s = document.createElement('script');
  s.async = true;
  s.src = META_PIXEL_SRC;
  document.head.appendChild(s);
  return fbq;
}

function send(event: MetaPixelEvent, params?: MetaPixelParams, pathname?: string): boolean {
  if (!metaPixelAllowed(pathname)) return false;
  const id = metaPixelId() as string;
  const fbq = load(id);
  if (revoked) {
    fbq('consent', 'grant');
    revoked = false;
  }
  if (params) fbq('track', event, params);
  else fbq('track', event);
  return true;
}

/** A conversion event (`Lead`, `CompleteRegistration`) with coarse parameters only. A no-op when the pixel is off, without consent or on any other page. */
export function track(event: Exclude<MetaPixelEvent, 'PageView'>, params?: MetaPixelParams): void {
  try {
    send(event, sanitizeParams(params as Record<string, unknown> | undefined));
  } catch {
    /* analytics never breaks a page */
  }
}

function pageView(): void {
  if (currentPath === null || pageViewFor === currentPath) return;
  if (send('PageView', undefined, currentPath)) pageViewFor = currentPath;
}

/** Retry the PageView of the current view (after the auth state resolved). */
export function retryPageView(): void {
  try {
    pageView();
  } catch {
    /* analytics never breaks a page */
  }
}

/** The SPA's route changed (or first rendered): one PageView per view of an allowed page, never twice for the same view. */
export function routeChanged(pathname: string): void {
  try {
    if (pathname !== currentPath) {
      currentPath = pathname;
      pageViewFor = null;
    }
    pageView();
  } catch {
    /* analytics never breaks a page */
  }
}

/** Meta's first-party cookies, on this host and each parent domain (the script sets them on the registrable domain). */
function clearMetaCookies(): void {
  const parts = window.location.hostname.split('.');
  const gone = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  for (const name of ['_fbp', '_fbc']) {
    document.cookie = `${name}=; ${gone}`;
    for (let i = 0; i < parts.length - 1; i++) document.cookie = `${name}=; ${gone}; domain=.${parts.slice(i).join('.')}`;
  }
}

/**
 * Follows the cookie banner: accepting analytics loads the pixel at once (no reload) and counts the page being viewed; withdrawing it tells the loaded
 * script to stop (consent revoke), removes Meta's cookies, and every later call is a no-op. Returns the unsubscribe function.
 */
export function watchConsent(): () => void {
  return onConsentChange((prefs) => {
    try {
      if (!metaPixelId()) return;
      if (prefs?.analytics === true) {
        pageView();
        return;
      }
      if (loaded && !revoked) {
        win().fbq?.('consent', 'revoke');
        revoked = true;
      }
      // also when this page never loaded the pixel: a cookie from an earlier visit goes with the "no"
      clearMetaCookies();
    } catch {
      /* analytics never breaks a page */
    }
  });
}

/** Tests only: forget the page-load state. */
export function resetMetaPixelForTests(): void {
  loaded = false;
  revoked = false;
  currentPath = null;
  pageViewFor = null;
  signedOut = false;
}
